import React, { useEffect, useMemo, useRef } from 'react'
import './opportunity-detail-container.scss'
import { Flex } from '../../lib/components'
import { OpportunityDetailTop } from './opportunity-detail-presenter-detail-top'
import { CMSOpportunityDetails } from './cms-components/cms-opportunity-details'
import { OpportunityDetailPresenterProps } from '../../lib/types'
import { ContactUsModal } from '../../lib/components/ContactUsModal'
import { resolveCmsImageUrl } from './cms-components/utils'
import OpportunityDetailHighlights from './opportunity-detail-highlights'
import OpportunityInvestmentProjection from './opportunity-detail-investment-projection'
import OpportunityDetailDocuments from './opportunity-detail-documents'
import { OpportunitySectionNavigationProvider } from './opportunity-section-navigation-context'
import { OpportunitySectionNavigationBar } from './opportunity-section-navigation-bar'
import { OpportunityDetailScrollToTop } from './opportunity-detail-scroll-to-top'
import {
  buildOpportunityNavSections,
  getSortedOpportunityDetailSanitySections,
  hasCmsTargetReturn,
} from './opportunity-detail.utils'

const OpportunityDetailPresenter = ({
  project,
  investHandleClick,
  indicateInterestHandleClick,
  isIndicatingInterest,
  backToProjectHandler,
  userInvestmentLatest,
  handleClickModal,
  showContactusModal,
  investButtonProperty,
  kycStatus,
  opportunityDetails,
}: OpportunityDetailPresenterProps) => {
  const heroImageUrl = resolveCmsImageUrl(opportunityDetails?.heroImage)
  const opportunityAccentColor = opportunityDetails?.opportunityColor?.backgroundColor
  const shellClassName = [
    'opportunity-overview-shell',
    heroImageUrl ? 'opportunity-overview-shell--has-hero' : '',
  ]
    .filter(Boolean)
    .join(' ')
  const shellStyle =
    opportunityAccentColor?.hex || heroImageUrl
      ? ({
          ...(opportunityAccentColor?.hex
            ? { '--opportunity-accent-color': opportunityAccentColor.hex }
            : {}),
          ...(heroImageUrl ? { '--opportunity-hero-image': `url("${heroImageUrl}")` } : {}),
        } as React.CSSProperties)
      : undefined

  const hasReturnMetric = hasCmsTargetReturn(opportunityDetails?.fees)
  const hasSanitySections = getSortedOpportunityDetailSanitySections(opportunityDetails).length > 0
  const navSections = useMemo(
    () => buildOpportunityNavSections(project, opportunityDetails),
    [project, opportunityDetails],
  )

  return (
    <OpportunitySectionNavigationProvider sections={navSections}>
      <div className='project-detail-main-layout'>
        {project && (
          <>
            <OpportunitySectionNavigationBar />
            <OpportunityDetailScrollToTop />
            <Flex classes={['detail-layout']}>
              <div className={shellClassName} style={shellStyle}>
                <div className='opportunity-overview-ribbon' />
                <div className='opportunity-overview-body'>
                  <OpportunityDetailTop
                    project={project}
                    opportunityDetails={opportunityDetails}
                    investHandleClick={investHandleClick}
                    indicateInterestHandleClick={indicateInterestHandleClick}
                    isIndicatingInterest={isIndicatingInterest}
                    backToProjectHandler={backToProjectHandler}
                    userInvestmentLatest={userInvestmentLatest}
                    investButtonProperty={investButtonProperty}
                    kycStatus={kycStatus}
                  />
                  <OpportunityDetailHighlights
                    project={project}
                    opportunityDetails={opportunityDetails}
                  />
                  {hasReturnMetric === true && (
                    <OpportunityInvestmentProjection
                      project={project}
                      expectedReturn={opportunityDetails?.fees?.targetReturn}
                    />
                  )}
                  {hasSanitySections ? (
                    <div
                      className='opportunity-cms-sections-stack'
                      data-testid='opportunity-detail-cms-expandable-stack'
                    >
                      <CMSOpportunityDetails
                        project={project}
                        opportunityDetails={opportunityDetails}
                        onMissingDataClick={handleClickModal}
                        layout='expandable'
                      />
                    </div>
                  ) : null}
                  <OpportunityDetailDocuments documents={project?.documents} />
                </div>
              </div>
            </Flex>
            {showContactusModal ? (
              <ContactUsModal active={true} subject={'Inquiry'} handleOnClose={handleClickModal} />
            ) : null}
          </>
        )}
      </div>
    </OpportunitySectionNavigationProvider>
  )
}

export { OpportunityDetailPresenter }
